import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AbTestSection, type AbSettings } from '@/components/marketing/campaigns/AbTestSection';
import { ApprovalBanner } from '@/components/marketing/campaigns/ApprovalBanner';
import { AudiencePicker } from '@/components/marketing/campaigns/AudiencePicker';
import { CampaignStatusControls } from '@/components/marketing/campaigns/CampaignStatusControls';
import { ReviewChecklist, type BuilderSection } from '@/components/marketing/campaigns/ReviewChecklist';
import { ScheduleDialog, type ScheduleProblem } from '@/components/marketing/campaigns/ScheduleDialog';
import { SenderSection } from '@/components/marketing/campaigns/SenderSection';
import {
  SequenceStepsEditor,
  stepsFromCampaign,
  stepsToInput,
  type StepDraft,
} from '@/components/marketing/campaigns/SequenceStepsEditor';
import { validateSteps } from '@/components/marketing/campaigns/step-rules';
import { TestSendDialog } from '@/components/marketing/campaigns/TestSendDialog';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { CAMPAIGN_STATUS_LABEL, campaignStatusTone } from '@/lib/marketing';
import type {
  ListPreview,
  MarketingCampaign,
  MarketingCampaignUpdateInput,
  MarketingList,
  MarketingUsableEmailAccount,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

const MODULE = 'marketing-campaigns' as const;
const LIST_PAGE = 100;

interface HeaderDraft extends AbSettings {
  name: string;
  emailAccountId: number | null;
  fromNameOverride: string;
  replyToOverride: string;
}

type HeaderKey = keyof HeaderDraft;
const HEADER_KEYS: readonly HeaderKey[] = [
  'name',
  'emailAccountId',
  'fromNameOverride',
  'replyToOverride',
  'abEnabled',
  'abSamplePercent',
  'abMetric',
  'abWaitHours',
];

interface AudienceDraft {
  include: number[];
  exclude: number[];
}

function headerFrom(c: MarketingCampaign): HeaderDraft {
  return {
    name: c.name,
    emailAccountId: c.emailAccount?.id ?? null,
    fromNameOverride: c.fromNameOverride ?? '',
    replyToOverride: c.replyToOverride ?? '',
    abEnabled: c.abEnabled,
    abSamplePercent: c.abSamplePercent,
    abMetric: c.abMetric,
    abWaitHours: c.abWaitHours,
  };
}

function audienceFrom(c: MarketingCampaign): AudienceDraft {
  return { include: c.audience.include.map((l) => l.id), exclude: c.audience.exclude.map((l) => l.id) };
}

function headerPatch(draft: HeaderDraft, base: HeaderDraft): MarketingCampaignUpdateInput {
  const patch: MarketingCampaignUpdateInput = {};
  if (draft.name.trim() !== base.name) patch.name = draft.name.trim();
  if (draft.emailAccountId !== base.emailAccountId) patch.emailAccountId = draft.emailAccountId;
  if (draft.fromNameOverride.trim() !== base.fromNameOverride)
    patch.fromNameOverride = draft.fromNameOverride.trim() === '' ? null : draft.fromNameOverride.trim();
  if (draft.replyToOverride.trim() !== base.replyToOverride)
    patch.replyToOverride = draft.replyToOverride.trim() === '' ? null : draft.replyToOverride.trim();
  if (draft.abEnabled !== base.abEnabled) patch.abEnabled = draft.abEnabled;
  if (draft.abSamplePercent !== base.abSamplePercent) patch.abSamplePercent = draft.abSamplePercent;
  if (draft.abMetric !== base.abMetric) patch.abMetric = draft.abMetric;
  if (draft.abWaitHours !== base.abWaitHours) patch.abWaitHours = draft.abWaitHours;
  return patch;
}

function sameSet(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((n) => set.has(n));
}

function stepsKey(steps: StepDraft[], kind: 'broadcast' | 'sequence', abEnabled: boolean): string {
  return JSON.stringify(stepsToInput(steps, { kind, abEnabled }));
}

function finishLabelFor(recipients: number | null, dailyLimit: number | null): string | null {
  if (recipients === null || dailyLimit === null || dailyLimit <= 0 || recipients <= 0) return null;
  const days = Math.ceil(recipients / dailyLimit);
  return days <= 1
    ? 'Finishes within a day'
    : `Takes about ${days} days at ${dailyLimit.toLocaleString()} a day`;
}

function sectionId(section: BuilderSection): string {
  return `campaign-section-${section}`;
}

function describe(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return fallback;
}

function Section({
  section,
  number,
  title,
  actions,
  children,
}: {
  section: BuilderSection;
  number: number;
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const headingId = `${sectionId(section)}-heading`;
  return (
    <Panel id={sectionId(section)} tabIndex={-1} aria-labelledby={headingId} className="min-w-0 scroll-mt-20 outline-none">
      <PanelHeader id={headingId} title={`${number} · ${title}`} actions={actions} />
      <PanelBody>{children}</PanelBody>
    </Panel>
  );
}

export function CampaignBuilderPage() {
  const params = useParams();
  const campaignId = Number(params.id);
  const { ability, user } = useAuth();
  const canRead = ability.canRead(MODULE);
  const canUpdate = ability.canUpdate(MODULE);
  const canSend = ability.canSend(MODULE);
  const canApprove = ability.canApprove(MODULE);
  const canReadSettings = ability.canRead('marketing-settings');
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const [campaign, setCampaign] = useState<MarketingCampaign | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // Drafts and the baselines they are compared against.
  const [header, setHeader] = useState<HeaderDraft | null>(null);
  const [headerBase, setHeaderBase] = useState<HeaderDraft | null>(null);
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [stepsBase, setStepsBase] = useState('');
  const [audience, setAudience] = useState<AudienceDraft>({ include: [], exclude: [] });
  const [audienceBase, setAudienceBase] = useState<AudienceDraft>({ include: [], exclude: [] });

  // Reference data.
  const [lists, setLists] = useState<MarketingList[] | null>(null);
  const [listsTruncated, setListsTruncated] = useState(false);
  const [listsError, setListsError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<{ id: number; name: string; subject: string | null }[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<MarketingUsableEmailAccount[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [threshold, setThreshold] = useState<number | null | undefined>(undefined);

  // Actions.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [headerErrors, setHeaderErrors] = useState<Partial<Record<HeaderKey, string>>>({});
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [serverProblems, setServerProblems] = useState<ScheduleProblem[]>([]);
  const [estimate, setEstimate] = useState<ListPreview | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const adopt = useCallback((c: MarketingCampaign) => {
    const h = headerFrom(c);
    const s = stepsFromCampaign(c);
    const a = audienceFrom(c);
    setCampaign(c);
    setHeader(h);
    setHeaderBase(h);
    setSteps(s);
    setStepsBase(stepsKey(s, c.kind, c.abEnabled));
    setAudience(a);
    setAudienceBase(a);
  }, []);

  useEffect(() => {
    if (!canRead || !Number.isFinite(campaignId)) return undefined;
    const controller = new AbortController();
    api
      .getMarketingCampaign(campaignId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setLoadError(null);
        adopt(result.data);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 404) setNotFound(true);
        else setLoadError(describe(caught, 'Could not reach the server.'));
      });
    return () => controller.abort();
  }, [canRead, campaignId, reloadToken, adopt]);

  useEffect(() => {
    if (!canRead) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listMarketingLists({ archived: false, perPage: LIST_PAGE }, signal)
      .then((r) => {
        if (signal.aborted) return;
        setLists(r.data);
        setListsTruncated(r.meta.total > r.data.length);
      })
      .catch((caught: unknown) => {
        if (!signal.aborted) setListsError(describe(caught, 'Could not reach the server.'));
      });
    api
      .listMarketingTemplates({ archived: false, perPage: LIST_PAGE }, signal)
      .then((r) => {
        if (signal.aborted) return;
        setTemplates(r.data.map((t) => ({ id: t.id, name: t.name, subject: t.subject })));
      })
      .catch((caught: unknown) => {
        if (!signal.aborted) setTemplatesError(describe(caught, 'Could not reach the server.'));
      });
    api
      .listUsableMarketingEmailAccounts(signal)
      .then((r) => {
        if (!signal.aborted) setAccounts(r.data);
      })
      .catch((caught: unknown) => {
        if (!signal.aborted) setAccountsError(describe(caught, 'Could not reach the server.'));
      });
    if (canReadSettings) {
      api
        .getMarketingSettings(signal)
        .then((r) => {
          if (!signal.aborted) setThreshold(r.data.approvalThreshold);
        })
        .catch(() => {
          /* Unknown threshold — the schedule dialog says "needs approval" without the number. */
        });
    }
    return () => controller.abort();
  }, [canRead, canReadSettings]);

  const kind = campaign?.kind ?? 'broadcast';
  const status = campaign?.status ?? 'draft';
  const headerEditable = Boolean(campaign?.editable) && canUpdate && (status === 'draft' || status === 'paused');
  const contentEditable = headerEditable && status === 'draft';

  const patch = useMemo(
    () => (header && headerBase ? headerPatch(header, headerBase) : {}),
    [header, headerBase],
  );
  const headerDirty = Object.keys(patch).length > 0;
  const stepsDirty = header !== null && stepsKey(steps, kind, header.abEnabled) !== stepsBase;
  const audienceDirty =
    !sameSet(audience.include, audienceBase.include) || !sameSet(audience.exclude, audienceBase.exclude);
  const dirty = headerDirty || (contentEditable && (stepsDirty || audienceDirty));

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // The editor does not validate itself: shape rules always, completeness once
  // somebody has tried to schedule.
  const [requireComplete, setRequireComplete] = useState(false);
  const localStepErrors = useMemo(
    () => validateSteps(steps, { kind, abEnabled: header?.abEnabled ?? false, requireComplete }),
    [steps, kind, header?.abEnabled, requireComplete],
  );
  const shapeErrorCount = useMemo(
    () => Object.keys(validateSteps(steps, { kind, abEnabled: header?.abEnabled ?? false })).length,
    [steps, kind, header?.abEnabled],
  );
  const editorErrors = useMemo(() => ({ ...localStepErrors, ...stepErrors }), [localStepErrors, stepErrors]);

  const selectedAccount = accounts?.find((a) => a.id === header?.emailAccountId) ?? null;
  const recipientCount = estimate?.count ?? campaign?.recipientEstimate ?? null;
  const finishLabel = finishLabelFor(recipientCount, selectedAccount?.dailyLimit ?? null);

  function jump(section: BuilderSection) {
    const el = document.getElementById(sectionId(section));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.focus({ preventScroll: true });
  }

  function distribute(problems: ScheduleProblem[]) {
    const nextHeader: Partial<Record<HeaderKey, string>> = {};
    const nextSteps: Record<string, string> = {};
    let nextAudience: string | null = null;
    for (const { path, message } of problems) {
      if ((HEADER_KEYS as readonly string[]).includes(path)) nextHeader[path as HeaderKey] = message;
      else if (path === 'steps' || path.startsWith('steps.')) nextSteps[path] = message;
      else if (path === 'audience' || path.startsWith('audience') || path.startsWith('include') || path.startsWith('exclude'))
        nextAudience = nextAudience ?? message;
    }
    setHeaderErrors(nextHeader);
    setStepErrors(nextSteps);
    setAudienceError(nextAudience);
  }

  async function save() {
    if (!campaign || !header || !headerBase) return;
    if (contentEditable && stepsDirty && shapeErrorCount > 0) {
      setSaveError('Nothing was saved. Fix the highlighted fields in the email content first.');
      jump('content');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveNotice(null);
    setHeaderErrors({});
    setStepErrors({});
    setAudienceError(null);
    setServerProblems([]);

    const saved: string[] = [];
    let latest: MarketingCampaign | null = null;
    const sendSteps = contentEditable && stepsDirty;
    const sendAudience = contentEditable && audienceDirty;
    const stepsBody = stepsToInput(steps, { kind, abEnabled: header.abEnabled });
    // Turning A/B off: drop variant B first, so the header change is not refused
    // over a B the server still holds. Turning it on: header first, so B is allowed.
    const stepsFirst = sendSteps && patch.abEnabled === false;

    async function putSteps() {
      const r = await api.replaceMarketingCampaignSteps(campaign!.id, stepsBody);
      latest = r.data;
      setStepsBase(JSON.stringify(stepsBody));
      saved.push('content');
    }
    async function patchHeader() {
      const r = await api.updateMarketingCampaign(campaign!.id, patch);
      latest = r.data;
      setHeaderBase(headerFrom(r.data));
      saved.push('details');
    }

    try {
      if (stepsFirst) await putSteps();
      if (headerDirty) await patchHeader();
      if (sendSteps && !stepsFirst) await putSteps();
      if (sendAudience) {
        const r = await api.replaceMarketingCampaignAudience(campaign.id, audience);
        latest = r.data;
        setAudienceBase(audience);
        setEstimate(null);
        saved.push('audience');
      }
      if (latest) adopt(latest);
      setSaveNotice('Draft saved.');
    } catch (caught) {
      if (latest) setCampaign(latest);
      if (caught instanceof ApiError && caught.details && caught.details.length > 0) {
        distribute(caught.details.map((d) => ({ path: d.path, message: d.message })));
      }
      const what = describe(caught, 'Could not reach the server.');
      setSaveError(
        saved.length > 0
          ? `Saved ${saved.join(' and ')}, but the rest did not save. ${what}`
          : `Nothing was saved. ${what}`,
      );
    } finally {
      setSaving(false);
    }
  }

  async function runEstimate() {
    if (!campaign) return;
    setEstimating(true);
    setEstimateError(null);
    try {
      const r = await api.estimateMarketingCampaign(campaign.id);
      setEstimate(r.data);
    } catch (caught) {
      setEstimateError(describe(caught, 'Could not reach the server.'));
    } finally {
      setEstimating(false);
    }
  }

  function openSchedule() {
    setRequireComplete(true);
    const complete = validateSteps(steps, { kind, abEnabled: header?.abEnabled ?? false, requireComplete: true });
    if (Object.keys(complete).length > 0) {
      jump('content');
      return;
    }
    setScheduleOpen(true);
  }

  const reload = useCallback(() => {
    setSaveNotice(null);
    setReloadToken((n) => n + 1);
  }, []);

  /* ── Refusals and load states ──────────────────────────────────────────── */

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title="Campaign" />
        <Panel>
          <EmptyState
            title="Campaigns are restricted"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  if (notFound || !Number.isFinite(campaignId)) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title="Campaign" />
        <Panel>
          <EmptyState
            title="Campaign not found"
            description="It may have been deleted."
            action={
              <TextLink to="/marketing/campaigns" className="text-sm font-medium">
                Back to campaigns
              </TextLink>
            }
          />
        </Panel>
      </div>
    );
  }

  if (!campaign || !header) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title={loadError ? 'Campaign' : <Skeleton className="h-6 w-56" />} />
        {loadError ? (
          <Notice
            tone="danger"
            role="alert"
            title={<>Couldn&rsquo;t load the campaign.</>}
            action={
              <button type="button" className="text-fg underline underline-offset-2" onClick={reload}>
                Try again
              </button>
            }
          >
            <span className="[overflow-wrap:anywhere]">{loadError}</span>
          </Notice>
        ) : (
          Array.from({ length: 4 }, (_, i) => (
            <Panel key={i}>
              <PanelBody>
                <div className="grid gap-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-9 w-full" />
                </div>
              </PanelBody>
            </Panel>
          ))
        )}
      </div>
    );
  }

  /* ── The builder ───────────────────────────────────────────────────────── */

  const savedVariants = campaign.steps.flatMap((step) =>
    step.variants.map((v) => ({
      id: v.id,
      label: `${kind === 'sequence' ? `Step ${step.stepOrder} · ` : ''}${campaign.abEnabled ? `${v.label} · ` : ''}${
        v.subject.trim() || 'No subject'
      }`,
    })),
  );

  const scheduleReason = !canSend
    ? null
    : status !== 'draft'
      ? null
      : dirty
        ? 'Save the draft first.'
        : null;

  const estimateDisabledReason =
    audience.include.length === 0
      ? 'Choose at least one list to send to.'
      : contentEditable && audienceDirty
        ? 'Save the draft to estimate these lists.'
        : null;

  const readOnlyNotice = !headerEditable
    ? status === 'pending_approval'
      ? 'Waiting for approval, so it cannot be edited.'
      : status === 'draft' || status === 'paused'
        ? 'Your account cannot edit this campaign.'
        : `${CAMPAIGN_STATUS_LABEL[status]} campaigns cannot be edited.`
    : status === 'paused'
      ? 'Paused: the sender and A/B settings can change; the audience and content are fixed once sending has started.'
      : null;

  const hasReport = status !== 'draft' && status !== 'pending_approval';

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader
        eyebrow={
          <TextLink to="/marketing/campaigns" tone="muted">
            Campaigns
          </TextLink>
        }
        title={
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="[overflow-wrap:anywhere]">{campaign.name}</span>
            <Badge tone={campaignStatusTone(status)}>{CAMPAIGN_STATUS_LABEL[status]}</Badge>
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {hasReport && (
              <Link
                to={`/marketing/campaigns/${campaign.id}/report`}
                className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm text-fg hover:bg-surface-hover max-sm:h-11"
              >
                View report
              </Link>
            )}
            {canSend && savedVariants.length > 0 && (
              <Button variant="secondary" onClick={() => setTestOpen(true)}>
                Send test
              </Button>
            )}
            {headerEditable && (
              <Button variant="secondary" onClick={() => void save()} loading={saving} disabled={!dirty}>
                Save draft
              </Button>
            )}
            {canSend && status === 'draft' && (
              <span title={scheduleReason ?? undefined} className="inline-flex">
                <Button variant="primary" onClick={openSchedule} disabled={scheduleReason !== null}>
                  Schedule
                </Button>
              </span>
            )}
          </div>
        }
      />

      <ApprovalBanner campaign={campaign} canApprove={canApprove} onChanged={reload} />

      {(status === 'scheduled' || status === 'sending' || status === 'paused' || status === 'pending_approval') && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface px-4 py-3 text-xs text-fg-secondary">
          <span>
            {campaign.startedAt
              ? `Started ${formatDateTime(campaign.startedAt)}`
              : campaign.scheduledAt
                ? `Scheduled for ${formatDateTime(campaign.scheduledAt)}${campaign.scheduleTimezone ? ` (${campaign.scheduleTimezone})` : ''}`
                : 'Sends as soon as it is approved'}
          </span>
          <CampaignStatusControls campaign={campaign} canSend={canSend} onChanged={reload} />
        </div>
      )}

      {campaign.lastError && (
        <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
          <span className="font-semibold text-fg">Last error:</span> {campaign.lastError}
        </Notice>
      )}

      {readOnlyNotice && <Notice tone="neutral">{readOnlyNotice}</Notice>}

      {saveError && (
        <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
          {saveError}
        </Notice>
      )}
      {saveNotice && !dirty && (
        <p role="status" className="m-0 text-xs text-success">
          {saveNotice}
        </p>
      )}

      <Section section="details" number={1} title="Details">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Name" required error={headerErrors.name}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={header.name}
                maxLength={200}
                disabled={!headerEditable}
                invalid={Boolean(headerErrors.name)}
                onChange={(e) => setHeader({ ...header, name: e.target.value })}
              />
            )}
          </Field>
          <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-1 self-end text-xs">
            <div>
              <dt className="text-fg-muted">Kind</dt>
              <dd className="m-0 text-fg-secondary">{kind === 'broadcast' ? 'Broadcast' : 'Sequence'}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">Created</dt>
              <dd className="m-0 text-fg-secondary">
                {formatDateTime(campaign.createdAt)}
                {campaign.createdBy ? ` by ${campaign.createdBy.name.trim() || campaign.createdBy.email}` : ''}
              </dd>
            </div>
          </dl>
        </div>
      </Section>

      <Section section="audience" number={2} title="Audience">
        <AudiencePicker
          lists={lists}
          listsLoading={lists === null && listsError === null}
          listsError={listsError}
          listsTruncated={listsTruncated}
          stored={campaign.audience}
          include={audience.include}
          exclude={audience.exclude}
          onChange={setAudience}
          disabled={!contentEditable}
          error={audienceError}
          estimate={estimate}
          estimating={estimating}
          estimateError={estimateError}
          estimateDisabledReason={estimateDisabledReason}
          onEstimate={() => void runEstimate()}
        />
      </Section>

      <Section section="sender" number={3} title="Sender">
        <SenderSection
          accounts={accounts}
          accountsLoading={accounts === null && accountsError === null}
          accountsError={accountsError}
          storedAccount={campaign.emailAccount}
          emailAccountId={header.emailAccountId}
          fromNameOverride={header.fromNameOverride}
          replyToOverride={header.replyToOverride}
          onChange={(p) => setHeader({ ...header, ...p })}
          disabled={!headerEditable}
          errors={headerErrors}
        />
      </Section>

      <Section section="content" number={4} title={kind === 'sequence' ? 'Emails' : 'Email'}>
        <div className="grid min-w-0 gap-5">
          {templatesError && (
            <Notice tone="danger" role="alert">
              Couldn&rsquo;t load templates. {templatesError}
            </Notice>
          )}
          {kind === 'broadcast' && (
            <AbTestSection
              value={header}
              onChange={(p) => setHeader({ ...header, ...p })}
              disabled={!headerEditable}
              errors={headerErrors}
            />
          )}
          <SequenceStepsEditor
            kind={kind}
            abEnabled={header.abEnabled}
            steps={steps}
            onChange={(next) => {
              setSteps(next);
              setStepErrors({});
            }}
            templates={templates}
            disabled={!contentEditable}
            errors={editorErrors}
          />
        </div>
      </Section>

      <Section section="review" number={5} title="Review">
        <ReviewChecklist
          name={header.name}
          kind={kind}
          emailAccountId={header.emailAccountId}
          includeCount={audience.include.length}
          steps={steps}
          abEnabled={header.abEnabled}
          dirty={dirty}
          recipientEstimate={recipientCount}
          finishLabel={finishLabel}
          serverProblems={serverProblems}
          onJump={jump}
        />
      </Section>

      {scheduleOpen && (
        <ScheduleDialog
          open
          onClose={() => setScheduleOpen(false)}
          campaignId={campaign.id}
          campaignName={campaign.name}
          timezone={timezone}
          recipientEstimate={recipientCount}
          needsApproval={campaign.needsApproval}
          approvalThreshold={threshold}
          finishLabel={finishLabel}
          onScheduled={() => {
            setScheduleOpen(false);
            reload();
          }}
          onIncomplete={(problems) => {
            setScheduleOpen(false);
            setServerProblems(problems);
            distribute(problems);
            window.setTimeout(() => jump('review'), 0);
          }}
        />
      )}

      {testOpen && (
        <TestSendDialog
          open
          onClose={() => setTestOpen(false)}
          campaignId={campaign.id}
          defaultEmail={user?.email ?? ''}
          variants={savedVariants}
          dirty={dirty}
        />
      )}
    </div>
  );
}
